"use client";

import clsx from "clsx";
import { motion } from "framer-motion";
import { useBillingPeriod } from "./MarketingPricingProvider";

export type MarketingPricingHeroProps = {
  title: string;
  subtitle?: string;
  yearlyLabel: string;
  monthlyLabel: string;
  saveBadge: string;
  toggleAriaLabel: string;
  className?: string;
};

type BillingOptionProps = {
  label: string;
  badge?: string;
  selected: boolean;
  onClick: () => void;
};

function BillingOption({ label, badge, selected, onClick }: BillingOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={clsx(
        "relative flex items-center gap-2 rounded-xl px-5 py-2 font-monument text-sm transition-colors",
        selected ? "text-white" : "text-black/60 hover:text-black",
      )}
    >
      {selected ? (
        <motion.span
          layoutId="billing-toggle-indicator"
          className="absolute inset-0 rounded-xl bg-black"
          transition={{ type: "spring", stiffness: 400, damping: 32 }}
        />
      ) : null}
      <span className="relative z-10">{label}</span>
      {badge ? (
        <span
          className={clsx(
            "relative z-10 rounded-md px-2 py-0.5 text-xs font-medium transition-colors",
            selected ? "bg-white/20 text-white" : "bg-black/10 text-black/60",
          )}
        >
          {badge}
        </span>
      ) : null}
    </button>
  );
}

export function MarketingPricingHero({
  title,
  subtitle,
  yearlyLabel,
  monthlyLabel,
  saveBadge,
  toggleAriaLabel,
  className,
}: Readonly<MarketingPricingHeroProps>) {
  const { billingPeriod, setBillingPeriod } = useBillingPeriod();

  return (
    <section className={clsx("flex justify-center px-4 pt-16 pb-12", className)}>
      <div className="flex w-full max-w-6xl flex-col items-center text-center">
        <h1 className="font-monument text-[2rem] font-normal leading-[1.12] tracking-tight text-black md:text-[2.5rem]">
          {title}
        </h1>

        {subtitle ? (
          <p className="mt-3 max-w-2xl font-monument text-lg leading-relaxed text-black/70 md:text-xl">
            {subtitle}
          </p>
        ) : null}

        <div
          role="group"
          aria-label={toggleAriaLabel}
          className="mt-8 inline-flex items-center gap-1 rounded-2xl border border-black/10 bg-marketing-voice-card p-1 md:mt-10"
        >
          <BillingOption
            label={yearlyLabel}
            badge={saveBadge}
            selected={billingPeriod === "yearly"}
            onClick={() => setBillingPeriod("yearly")}
          />
          <BillingOption
            label={monthlyLabel}
            selected={billingPeriod === "monthly"}
            onClick={() => setBillingPeriod("monthly")}
          />
        </div>
      </div>
    </section>
  );
}
